import React, { useCallback, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import {
  RewardsRail,
  theme,
  useContainerSpacing,
  useResponsive,
} from '@sg/garnish';

import { useChallengesAndRewards } from '@order/ChallengesAndRewards';
import { useRewardsWithDerivedData } from '@order/hooks';
import { useLocalizationContext } from '@order/Localization';

import { RewardDetailsModal } from '../../../../../../components/RewardDetailsModal';
import { IllusRewards } from '../../assets';
import { ChallengesScreenInfoPanel } from '../ChallengesScreenInfoPanel';

// ────────────────────────────────────────────────────────────────────────────────

export const ChallengesScreenRewards = () => (
  <View style={styles.wrapper}>
    <ChallengesScreenRewardsContent />
  </View>
);

// ─── SUBCOMPONENTS ──────────────────────────────────────────────────────────────

const ChallengesScreenRewardsContent = () => {
  const {
    challengeRewards,
    isFetchingChallengeRewards,
    challengeRewardsError,
  } = useChallengesAndRewards();

  const isLoadingChallengeRewards =
    challengeRewards === undefined || isFetchingChallengeRewards;
  const hasAvailableRewards =
    Array.isArray(challengeRewards) && challengeRewards.length > 0;
  const shouldRenderRewardsRail =
    isLoadingChallengeRewards || hasAvailableRewards;

  // ─────────────────────────────────────────────────────────────────

  if (challengeRewardsError) return <ChallengesScreenRewardsError />;

  if (shouldRenderRewardsRail) return <ChallengesScreenRewardsRail />;

  return <ChallengesScreenNoRewards />;
};

const ChallengesScreenRewardsRail = () => {
  const { challengeRewards, isFetchingChallengeRewards } =
    useChallengesAndRewards();
  const { t } = useLocalizationContext();
  const { minWidth } = useResponsive();
  const rewardsWithExtraDetails = useRewardsWithDerivedData(
    challengeRewards ?? [],
  );

  // ─────────────────────────────────────────────────────────────────

  const [activeRewardId, setActiveRewardId] = useState('');

  const resetActiveRewardId = useCallback(() => {
    setActiveRewardId('');
  }, []);

  const activeReward = rewardsWithExtraDetails?.find(
    (reward) => reward.id === activeRewardId,
  );

  const containerSpacing = useContainerSpacing();

  // ─────────────────────────────────────────────────────────────────

  const isLoadingChallengeRewards =
    rewardsWithExtraDetails === undefined || isFetchingChallengeRewards;

  return (
    <View>
      <RewardsRail
        rewards={rewardsWithExtraDetails}
        isLoading={isLoadingChallengeRewards}
        headerText={t('challenges.rewards.header')}
        footerText={t('challenges.rewards.railFooterText')}
        headerVariation={minWidth.tablet ? 'display' : 'body'}
        outerOffset={-containerSpacing}
        onPress={setActiveRewardId}
        withoutHeaderBorder={minWidth.tablet}
      />

      <RewardDetailsModal
        reward={activeReward}
        onDismiss={resetActiveRewardId}
      />
    </View>
  );
};

const ChallengesScreenNoRewards = () => {
  const { t } = useLocalizationContext();

  return (
    <ChallengesScreenInfoPanel
      heading={t('challenges.rewards.header')}
      text={t('challenges.rewards.noRewardsText')}
      illustrationSource={IllusRewards}
      testID="challenges.rewards.noRewards"
    />
  );
};

const ChallengesScreenRewardsError = () => {
  const { t } = useLocalizationContext();

  return (
    <ChallengesScreenInfoPanel
      heading={t('challenges.rewards.header')}
      text={t('challenges.rewards.error.network-error')}
      illustrationSource={IllusRewards}
    />
  );
};

// ─── STYLES ─────────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  wrapper: {
    paddingTop: theme.spacing.small,
    paddingBottom: theme.spacing.xlarge,
  },
});
